import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'motion/react';
import './GuideAdeef.css';

const FALLBACK_TIMELINE = [
  {
    title: 'Avant votre arrivée',
    summary: 'Documents nécessaires, visa, assurance et préparatifs essentiels.',
    items: [
      'Obtention du visa étudiant',
      'Assurance santé obligatoire',
      'Documents à préparer',
      'Réservation du logement'
    ]
  },
  {
    title: 'Trouver un logement',
    summary: 'Tout savoir sur la recherche de logement étudiant en France.',
    items: [
      'Types de logements',
      'Résidences Nexity Studéa',
      'Dossier de location',
      'Aides au logement (APL)'
    ]
  },
  {
    title: 'Démarches administratives',
    summary: 'Compte bancaire, sécurité sociale, titre de séjour et aides.',
    items: [
      'Compte bancaire français',
      'Sécurité sociale étudiante',
      'Titre de séjour',
      'CAF et allocations'
    ]
  },
  {
    title: 'Vie étudiante',
    summary: 'S’intégrer et réussir son parcours universitaire en France.',
    items: [
      'Inscription universitaire',
      'Bibliothèques',
      'Restaurants universitaires',
      'Associations étudiantes'
    ]
  }
];

const EMOJI_REGEX = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\uFE0F]/gu;

const FALLBACK_PARSED = FALLBACK_TIMELINE.map((entry, index) => ({
  id: `fallback-${index + 1}`,
  unitRoman: 'I',
  unitTitle: 'Guide ADEEF',
  chapterNumber: index + 1,
  title: entry.title,
  displayTitle: `${index + 1}. ${entry.title}`,
  summary: entry.summary,
  items: entry.items,
  details: [],
}));

function renderInlineContent(text, keyPrefix) {
  const value = String(text || '');
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/gi;
  const nodes = [];
  let lastIndex = 0;
  let match;
  let i = 0;

  while ((match = linkRegex.exec(value)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(value.slice(lastIndex, match.index));
    }

    const label = (match[1] || '').trim() || match[2];
    const href = (match[2] || '').trim();
    nodes.push(
      <a key={`${keyPrefix}-link-${i}`} href={href} target="_blank" rel="noreferrer noopener">
        {label}
      </a>
    );

    i += 1;
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < value.length) {
    nodes.push(value.slice(lastIndex));
  }

  return nodes;
}

function parseGuideText(text) {
  if (!text || !text.trim()) return FALLBACK_PARSED;

  const lines = text.split(/\r?\n/);
  const romanRe = /^([IVXLCDM]+)\.\s+(.+)$/i;
  const chapterRe = /^(\d{1,2})\s*[.)-]?\s+(.+)$/u;

  const chapters = [];
  const units = [];

  let currentUnit = null;
  let currentChapter = null;
  let chapterIdCounter = 1;
  let bodyMode = false;

  const toComparable = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const cleanText = (value) => String(value || '')
    .replace(EMOJI_REGEX, '')
    .replace(/\\([-.()#+*])/g, '$1')
    .replace(/\\\+/g, '+')
    .replace(/^\\\s*([→➜›•*+-])\s*/u, '')
    .replace(/^#+\s*/g, '')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/^\s*[✅☑✔✳‼!]+\s*/u, '')
    .replace(/^\s*[→➜›•*-]+\s*/u, '')
    .replace(/^#+\s*/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const cleanChapterTitle = (value) => cleanText(value)
    .replace(/[✅☑✔]+$/u, '')
    .trim();

  const asChapterHeading = (rawLine) => {
    const cleaned = cleanText(rawLine);
    if (!cleaned || romanRe.test(cleaned)) return null;
    const m = cleaned.match(chapterRe);
    if (!m) return null;
    const chapterNumber = Number(m[1]);
    const chapterTitle = cleanChapterTitle(m[2]);
    if (!chapterTitle) return null;
    return { chapterNumber, chapterTitle };
  };

  const asUnitHeading = (rawLine) => {
    const cleaned = cleanText(rawLine);
    const m = cleaned.match(romanRe);
    if (!m) return null;
    return {
      roman: String(m[1] || '').toUpperCase(),
      title: cleanText(m[2]),
    };
  };

  const addUnit = (unit) => {
    const existing = units.find((u) => u.roman === unit.roman);
    if (existing) {
      currentUnit = existing;
      return;
    }
    const created = { ...unit, chapterIds: [] };
    units.push(created);
    currentUnit = created;
  };

  const addIndexPoint = (rawLine) => {
    if (!currentChapter) return;
    const line = String(rawLine || '').trim();
    if (!line) return;
    const hasArrow = /→|➜|›/.test(line);
    const pointMatch = line.match(/(?:→|➜|›)\s*(.+)$/u);
    const point = cleanText(pointMatch ? pointMatch[1] : line);
    if (!point) return;
    if (!hasArrow && /^\d+\)/.test(point)) return;
    if (!hasArrow && point.length > 120) return;
    currentChapter.indexPoints.push(point);
  };

  const attachDetail = (chapter, rawLine) => {
    const line = String(rawLine || '').trim();
    if (!line) return;

    const cleaned = cleanText(line);
    if (!cleaned) return;

    const plusCalloutMatch = line.match(/^\\?\+\s*(.+)$/u);
    if (plusCalloutMatch) {
      const value = cleanText(plusCalloutMatch[1]);
      if (value) chapter.details.push({ type: 'plus', text: `+ ${value}` });
      return;
    }

    if (/^NB\s*[:：]?/iu.test(cleaned)) {
      chapter.details.push({ type: 'note', text: cleaned.replace(/^NB\s*[:：]?\s*/iu, 'NB : ') });
      return;
    }

    if (/^(?:\\[-+*]\s+|\*\s+|[-+]\s+|\d+[.)]\s+|→\s*)/u.test(line)) {
      const value = cleaned.replace(/^\d+[.)]\s+/u, '').trim();
      if (value) chapter.details.push({ type: 'bullet', text: value });
      return;
    }

    const headingLike = /^#{2,6}\s+/u.test(line)
      || (/^\*\*.+\*\*$/u.test(line) && cleaned.length <= 110)
      || (/^[A-ZÉÀÙÂÊÎÔÛÇ].{0,90}$/u.test(cleaned) && /:\s*$/.test(cleaned));

    if (headingLike) {
      chapter.details.push({ type: 'subheading', text: cleaned.replace(/:\s*$/, '') });
      return;
    }

    chapter.details.push({ type: 'paragraph', text: cleaned });
  };

  const titleMap = new Map();

  for (const rawLine of lines) {
    const raw = String(rawLine || '');
    const trimmed = raw.trim();
    if (!trimmed) continue;

    if (!bodyMode && /^#\s+[IVXLCDM]+\./iu.test(trimmed)) {
      bodyMode = true;
    }

    if (!bodyMode) {
      const unit = asUnitHeading(trimmed);
      if (unit) {
        addUnit(unit);
        currentChapter = null;
        continue;
      }

      const chapter = asChapterHeading(trimmed);
      if (chapter) {
        if (!currentUnit) {
          addUnit({ roman: 'I', title: 'Guide ADEEF' });
        }
        const createdChapter = {
          id: `chapter-${chapterIdCounter}`,
          unitRoman: currentUnit.roman,
          unitTitle: currentUnit.title,
          chapterNumber: chapter.chapterNumber,
          title: chapter.chapterTitle,
          indexPoints: [],
          details: [],
        };
        chapterIdCounter += 1;
        chapters.push(createdChapter);
        currentUnit.chapterIds.push(createdChapter.id);
        titleMap.set(`${currentUnit.roman}|${toComparable(chapter.chapterTitle)}`, createdChapter);
        titleMap.set(`*|${toComparable(chapter.chapterTitle)}`, titleMap.get(`*|${toComparable(chapter.chapterTitle)}`) || createdChapter);
        currentChapter = createdChapter;
        continue;
      }

      addIndexPoint(trimmed);
      continue;
    }

    const unit = asUnitHeading(trimmed);
    if (unit) {
      currentUnit = units.find((u) => u.roman === unit.roman) || currentUnit;
      currentChapter = null;
      continue;
    }

    const chapter = asChapterHeading(trimmed);
    if (chapter) {
      const keyWithUnit = currentUnit ? `${currentUnit.roman}|${toComparable(chapter.chapterTitle)}` : '';
      const keyAny = `*|${toComparable(chapter.chapterTitle)}`;
      const matchedChapter = titleMap.get(keyWithUnit) || titleMap.get(keyAny) || null;
      if (matchedChapter) {
        currentChapter = matchedChapter;
        continue;
      }

      // Numbered headings inside a chapter body (e.g. 1. Janvier - Mars)
      // should remain part of the current chapter as sub-sections.
      if (currentChapter) {
        attachDetail(currentChapter, trimmed);
      }
      continue;
    }

    if (currentChapter) {
      attachDetail(currentChapter, trimmed);
    }
  }

  const parsed = chapters
    .map((chapter) => {
      const uniquePoints = Array.from(new Set(chapter.indexPoints.map(cleanText).filter(Boolean))).slice(0, 10);
      const summary = uniquePoints[0] || '';
      const items = uniquePoints.slice(1);

      return {
        id: chapter.id,
        unitRoman: chapter.unitRoman,
        unitTitle: chapter.unitTitle,
        chapterNumber: chapter.chapterNumber,
        title: chapter.title,
        displayTitle: `${chapter.chapterNumber}. ${chapter.title}`,
        summary,
        items,
        details: chapter.details,
      };
    })
    .filter((chapter) => chapter.title);

  return parsed.length ? parsed : FALLBACK_PARSED;
}

function GuideTimeline({ data, chapterRefs }) {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const computeHeight = () => {
      if (!contentRef.current) return;
      const rawHeight = contentRef.current.scrollHeight;
      const safeHeight = Math.max(rawHeight - 40, 0);
      setHeight(safeHeight);
    };

    computeHeight();
    window.addEventListener('resize', computeHeight);
    return () => window.removeEventListener('resize', computeHeight);
  }, [data]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start 12%', 'end 55%'],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.12], [0, 1]);

  return (
    <section className="guide-timeline" ref={containerRef}>
      <div className="guide-timeline-head">
        <h2>Parcours étudiant en France</h2>
        <p>Suivez les étapes clés et les démarches importantes, organisées en timeline.</p>
      </div>

      <div ref={contentRef} className="guide-timeline-content">
        {data.map((entry, index) => (
          <article
            key={`${entry.title}-${index}`}
            className="timeline-row"
            ref={(node) => {
              chapterRefs.current[index] = node;
            }}
          >
            <div className="timeline-sticky-col">
              <div className="timeline-dot-wrap">
                <span className="timeline-dot" />
              </div>
              <motion.div
                className="timeline-heading-wrap"
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: Math.min(index * 0.03, 0.12) }}
              >
                <span className="timeline-unit-label">{entry.unitRoman}. {entry.unitTitle}</span>
                <h3>{entry.displayTitle || entry.title}</h3>
              </motion.div>
            </div>

            <motion.div
              className="timeline-card"
              initial={{ opacity: 0, x: -42 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: Math.min(index * 0.04, 0.18) }}
            >
              <motion.h4
                className="timeline-mobile-title"
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1], delay: 0.02 }}
              >
                {entry.displayTitle || entry.title}
              </motion.h4>
              {entry.details?.length ? (
                <motion.div
                  className="timeline-details"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={{
                    hidden: {},
                    visible: {
                      transition: {
                        staggerChildren: 0.045,
                        delayChildren: 0.02,
                      },
                    },
                  }}
                >
                  {entry.details.map((block, blockIndex) => {
                    const key = `${entry.id}-detail-${blockIndex}`;
                    if (block.type === 'subheading') {
                      return (
                        <motion.h5
                          key={key}
                          variants={{
                            hidden: { opacity: 0, x: -16 },
                            visible: { opacity: 1, x: 0 },
                          }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        >
                          {renderInlineContent(block.text, key)}
                        </motion.h5>
                      );
                    }
                    if (block.type === 'plus') {
                      return (
                        <motion.p
                          key={key}
                          className="timeline-detail-plus"
                          variants={{
                            hidden: { opacity: 0, x: -14 },
                            visible: { opacity: 1, x: 0 },
                          }}
                          transition={{ duration: 0.33, ease: [0.22, 1, 0.36, 1] }}
                        >
                          {renderInlineContent(block.text, key)}
                        </motion.p>
                      );
                    }
                    if (block.type === 'note') {
                      return (
                        <motion.p
                          key={key}
                          className="timeline-detail-note"
                          variants={{
                            hidden: { opacity: 0, x: -14 },
                            visible: { opacity: 1, x: 0 },
                          }}
                          transition={{ duration: 0.33, ease: [0.22, 1, 0.36, 1] }}
                        >
                          {renderInlineContent(block.text, key)}
                        </motion.p>
                      );
                    }
                    if (block.type === 'bullet') {
                      return (
                        <motion.p
                          key={key}
                          className="timeline-detail-bullet"
                          variants={{
                            hidden: { opacity: 0, x: -14 },
                            visible: { opacity: 1, x: 0 },
                          }}
                          transition={{ duration: 0.33, ease: [0.22, 1, 0.36, 1] }}
                        >
                          • {renderInlineContent(block.text, key)}
                        </motion.p>
                      );
                    }
                    return (
                      <motion.p
                        key={key}
                        variants={{
                          hidden: { opacity: 0, x: -12 },
                          visible: { opacity: 1, x: 0 },
                        }}
                        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                      >
                        {renderInlineContent(block.text, key)}
                      </motion.p>
                    );
                  })}
                </motion.div>
              ) : null}
            </motion.div>
          </article>
        ))}

        <div className="timeline-line-track" style={{ height: `${height}px` }}>
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="timeline-line-progress"
          />
        </div>
      </div>
    </section>
  );
}

const GuideAdeef = () => {
  const [timelineData, setTimelineData] = useState(FALLBACK_PARSED);
  const chapterRefs = useRef([]);

  useEffect(() => {
    const hasManualScrollRestoration = 'scrollRestoration' in window.history;
    const previousScrollRestoration = hasManualScrollRestoration
      ? window.history.scrollRestoration
      : null;

    if (hasManualScrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    return () => {
      if (hasManualScrollRestoration && previousScrollRestoration) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, []);

  const indexUnits = [];
  const unitMap = new Map();
  timelineData.forEach((chapter, index) => {
    const unitKey = `${chapter.unitRoman || ''}-${chapter.unitTitle || ''}`;
    if (!unitMap.has(unitKey)) {
      const unitValue = {
        key: unitKey,
        unitRoman: chapter.unitRoman || 'I',
        unitTitle: chapter.unitTitle || 'Guide ADEEF',
        chapters: [],
      };
      unitMap.set(unitKey, unitValue);
      indexUnits.push(unitValue);
    }

    unitMap.get(unitKey).chapters.push({
      index,
      id: chapter.id,
      displayTitle: chapter.title,
      points: [chapter.summary, ...(chapter.items || [])].filter(Boolean),
    });
  });

  const unitButtons = indexUnits
    .map((unit) => ({
      key: unit.key,
      label: `${unit.unitRoman}. ${unit.unitTitle}`,
      targetIndex: unit.chapters[0]?.index ?? 0,
    }))
    .slice(0, 4);

  const scrollToChapter = (index) => {
    const target = chapterRefs.current[index];
    if (!target) return;

    const y = target.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  useEffect(() => {
    const candidates = [
      '/GUIDE%20%C3%89TUDIANTS.md',
      '/GUIDE%20%C3%89TUDIANTS.txt',
      '/GUIDE%20ETUDIANTS.md',
      '/GUIDE%20ETUDIANTS.txt',
      '/assets/GUIDE%20%C3%89TUDIANTS.md',
      '/assets/GUIDE%20%C3%89TUDIANTS.txt',
      '/assets/GUIDE%20ETUDIANTS.md',
      '/assets/GUIDE%20ETUDIANTS.txt',
    ];

    let cancelled = false;

    const loadGuide = async () => {
      for (const path of candidates) {
        try {
          const res = await fetch(path, { cache: 'no-store' });
          if (!res.ok) continue;
          const text = await res.text();
          if (/^\s*<!doctype html>/i.test(text) || /^\s*<html/i.test(text)) continue;
          const parsed = parseGuideText(text);
          if (!cancelled && parsed.length) {
            setTimelineData(parsed);
            return;
          }
        } catch (e) {
          // try next path
        }
      }
    };

    void loadGuide();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="guide-page">
      <section className="guide-hero">
        <div className="hero-content">
          <h1>Guide ADEEF</h1>
          <p>Votre guide complet pour réussir votre vie étudiante en France</p>
        </div>
      </section>

      <section className="guide-intro">
        <div className="container">
          <div className="section-card">
            <div className="intro-content">
              <h2>Bienvenue en France !</h2>
              <p>
                Ce guide a été structuré pour vous accompagner étape par étape, depuis la
                préparation du départ jusqu’à votre intégration en France.
              </p>
              <nav className="chapter-selector" aria-label="Index du guide">
                {unitButtons.map((unit) => (
                  <motion.button
                    key={unit.key}
                    type="button"
                    className="chapter-selector-btn"
                    whileTap={{ scaleX: 0.94, scaleY: 0.94 }}
                    transition={{ duration: 0.12, ease: [0.2, 0.8, 0.2, 1] }}
                    onClick={() => {
                      scrollToChapter(unit.targetIndex);
                    }}
                  >
                    {unit.label}
                  </motion.button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </section>

      <div className="container guide-timeline-container">
        <div className="guide-timeline-wrap">
          <GuideTimeline data={timelineData} chapterRefs={chapterRefs} />
        </div>
      </div>

      <section className="faq-cta">
        <div className="container">
          <div className="section-card">
            <h2>Des questions ?</h2>
            <p>Consultez notre FAQ ou contactez-nous directement</p>
            <div className="cta-buttons">
              <Link to="/faq" className="btn-primary">Voir la FAQ</Link>
              <Link to="/contact" className="btn-secondary">Nous contacter</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuideAdeef;
